import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Heart, Users, MessageCircle, Smile, Brain, Shield, Star } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqCategories = [
    {
      category: "Getting Started",
      icon: Heart,
      color: "from-pink-500 to-rose-500",
      questions: [
        {
          question: "What is EternitySingles®?",
          answer: "EternitySingles® is a unique dating platform that combines romance with wellness. We help you find meaningful connections while prioritising your mental health and emotional well-being. Our app features mood tracking, wellness resources, support groups, and mindful matching to ensure you're in the best emotional space for love."
        },
        {
          question: "How do I create an account?",
          answer: "Simply click 'Get Started' on the landing page, fill in your email, create a password, and complete your profile. You can add photos, answer personality questions, and share what you're looking for in a partner."
        },
        {
          question: "Is EternitySingles® free to use?",
          answer: "Yes! EternitySingles® offers a comprehensive free experience including profile creation, mood tracking, wellness resources, support groups, and basic matching features. We believe everyone deserves access to mental wellness and meaningful connections."
        }
      ]
    },
    {
      category: "Wellness Features",
      icon: Brain,
      color: "from-purple-500 to-indigo-500",
      questions: [
        {
          question: "How does Mood Tracking work?",
          answer: "Our Mood Tracker allows you to log your daily emotional state with just a few clicks. Track patterns over time, receive AI-powered insights, and get personalized wellness suggestions. Understanding your emotional patterns helps you date from a healthier mindset."
        },
        {
          question: "What are Support Groups?",
          answer: "Support Groups are safe, moderated community spaces where members can share experiences, offer encouragement, and discuss topics like dating anxiety, self-care, mindfulness, and relationship wellness. You can join public groups or create private ones for closer circles."
        },
        {
          question: "What kind of Wellness Resources are available?",
          answer: "We offer curated mental health resources including articles, meditation guides, breathing exercises, self-care tips, and professional advice. These resources are organized by topic to help you find exactly what you need when you need it."
        },
        {
          question: "What are Relax Games?",
          answer: "Relax Games are mindfulness-based mini-games designed to help you de-stress and practice being present. From breathing exercises to gentle interactive experiences, these tools help you center yourself before engaging in the dating world."
        }
      ]
    },
    {
      category: "Matching & Dating",
      icon: Star,
      color: "from-amber-500 to-orange-500",
      questions: [
        {
          question: "How does matching work?",
          answer: "Our algorithm considers your preferences, interests, values, and wellness compatibility. We don't just match based on looks – we prioritize emotional readiness and shared values to create more meaningful connections."
        },
        {
          question: "What's the difference between Matches and Favorites?",
          answer: "Matches are profiles you've mutually connected with, meaning both parties showed interest. Favorites are profiles you've saved for later consideration. You can browse your Favorites anytime and send them a like when you're ready."
        },
        {
          question: "How do I start a conversation?",
          answer: "Once you've matched with someone, simply go to your Messages tab and start chatting! We encourage mindful, authentic communication. Take your time, be yourself, and remember – quality over quantity."
        },
        {
          question: "Can I undo a swipe?",
          answer: "Currently, swipes are final, so we encourage you to take your time when browsing profiles. This mindful approach helps you make more intentional choices about potential connections."
        }
      ]
    },
    {
      category: "Safety & Privacy",
      icon: Shield,
      color: "from-blue-500 to-cyan-500",
      questions: [
        {
          question: "Is my data safe?",
          answer: "Absolutely. We use industry-standard encryption to protect your personal information. Your mood data, messages, and profile details are stored securely and never shared with third parties without your explicit consent."
        },
        {
          question: "Can I block or report users?",
          answer: "Yes. Your safety is our priority. You can block any user from their profile or message thread. You can also report users for inappropriate behavior through the three-dot menu. All reports are reviewed by our moderation team."
        },
        {
          question: "How do I control my privacy settings?",
          answer: "Visit Settings to manage your privacy preferences. You can control who sees your profile, manage your photo visibility, adjust notification settings, and more. You're in complete control of your experience."
        },
        {
          question: "What happens to my data if I delete my account?",
          answer: "When you delete your account from Settings, all your personal data including profile information, messages, and mood tracking data will be permanently removed from our servers within 30 days."
        }
      ]
    },
    {
      category: "Account & Settings",
      icon: Users,
      color: "from-emerald-500 to-green-500",
      questions: [
        {
          question: "How do I edit my profile?",
          answer: "Click on your profile icon in the top right corner and select 'Profile'. From there, you can edit your photos, bio, interests, and preferences. Keep your profile authentic – it helps attract the right matches!"
        },
        {
          question: "Can I change my email or password?",
          answer: "Yes! Go to Settings, then select 'Account' to update your email address or change your password. For security, you'll need to verify your current password before making changes."
        },
        {
          question: "How do I enable/disable notifications?",
          answer: "Visit Settings > Notifications to customize which alerts you receive. You can control notifications for new matches, messages, support group activity, mood tracking reminders, and more."
        },
        {
          question: "Can I pause my account instead of deleting it?",
          answer: "Currently, we offer account deletion through Settings. If you need a break, consider adjusting your notification settings or simply taking time away from the app. Your account will remain secure until you return."
        }
      ]
    },
    {
      category: "Support & Help",
      icon: MessageCircle,
      color: "from-indigo-500 to-purple-500",
      questions: [
        {
          question: "I'm having technical issues. What should I do?",
          answer: "Try these steps first: 1) Clear your browser cache, 2) Hard refresh the page (Ctrl+Shift+R), 3) Try an incognito window. If issues persist, report them through the 'Report Issue' option in Support Groups or contact our support team."
        },
        {
          question: "How can I provide feedback?",
          answer: "We love hearing from our community! You can provide feedback through Settings > Help & Feedback, or use the 'Report Issue' feature available in Support Groups. Your input helps us improve the app for everyone."
        },
        {
          question: "Where can I find more mental health resources?",
          answer: "Visit the Wellness tab to access our curated library of mental health resources, articles, and professional guidance. For immediate crisis support, please contact a mental health professional or crisis helpline in your area."
        },
        {
          question: "Do you have a mobile app?",
          answer: "EternitySingles® is currently a web-based platform optimized for both desktop and mobile browsers. This allows us to update features quickly and ensure a consistent experience across all devices."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-emerald-800 dark:text-emerald-100 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-emerald-600 dark:text-emerald-300 max-w-2xl mx-auto">
            Find answers to common questions about EternitySingles®. We're here to help you navigate your wellness and dating journey.
          </p>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqCategories.map((category, categoryIndex) => {
            const CategoryIcon = category.icon;
            return (
              <div key={categoryIndex} className="card animate-fade-in-up">
                {/* Category Header */}
                <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-emerald-200 dark:border-slate-700">
                  <div className={`w-12 h-12 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center`}>
                    <CategoryIcon className="w-6 h-6 text-white" strokeWidth={2} />
                  </div>
                  <h2 className="text-2xl font-bold text-emerald-800 dark:text-emerald-100">
                    {category.category}
                  </h2>
                </div>

                {/* Questions */}
                <div className="space-y-3">
                  {category.questions.map((faq, faqIndex) => {
                    const globalIndex = `${categoryIndex}-${faqIndex}`;
                    const isOpen = openIndex === globalIndex;
                    
                    return (
                      <div
                        key={faqIndex}
                        className="border border-emerald-200 dark:border-slate-700 rounded-xl overflow-hidden transition-all hover:shadow-md"
                      >
                        <button
                          onClick={() => toggleFAQ(globalIndex)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-emerald-50 dark:hover:bg-slate-800 transition-colors"
                        >
                          <span className="font-semibold text-emerald-800 dark:text-emerald-100 pr-4">
                            {faq.question}
                          </span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <div className="px-6 pb-4 pt-2 bg-emerald-50/50 dark:bg-slate-800/50 animate-fade-in">
                            <p className="text-emerald-700 dark:text-emerald-200 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact Section */}
        <div className="mt-12 card text-center bg-gradient-to-r from-emerald-500 to-green-600 text-white">
          <Heart className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h3 className="text-2xl font-bold mb-2">Still have questions?</h3>
          <p className="mb-6 opacity-90">
            We're here to help! Reach out to our support team through Settings or join our Support Groups community.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="mailto:info@eternitysingles.com"
              className="bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors shadow-lg"
            >
              Contact Support
            </a>
            <a
              href="/groups"
              className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors border-2 border-white"
            >
              Join Community
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
